import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { throwError } from 'rxjs';
import { catchError, exhaustMap, finalize, take, tap } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { LoaderService } from './loader.service';
import { LoginService } from './login.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
    private readonly SKIP_ACTIVITY_HEADER = 'X-Skip-Loading';

    // Set by callers authenticating as a SECOND identity on top of the staff session (e.g. the
    // RMS Simulator, which calls the external RMS API as a wholesaler via RMS-X-AUTH-KEY while
    // still carrying the portal's own X-Auth-Key). A 401 from that second identity being wrong/
    // expired/rotated is a normal, expected outcome to show inline - it must never be treated as
    // the STAFF session itself being invalid and log them out of the whole portal.
    private readonly SKIP_AUTH_LOGOUT_HEADER = 'X-Skip-Auth-Logout';

    constructor(
        private loginService: LoginService,
        private logger: NGXLogger,
        private errorHandlerService: ErrorHandlerService,
        private toasterService: ToastrService,
        private loaderService: LoaderService,
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        const shouldSkipActivity = req.headers.has(this.SKIP_ACTIVITY_HEADER);
        const shouldSkipAuthLogout = req.headers.has(this.SKIP_AUTH_LOGOUT_HEADER);

        let cleanedReq = req;
        if (shouldSkipActivity) {
            cleanedReq = cleanedReq.clone({ headers: cleanedReq.headers.delete(this.SKIP_ACTIVITY_HEADER) });
        }
        if (shouldSkipAuthLogout) {
            cleanedReq = cleanedReq.clone({ headers: cleanedReq.headers.delete(this.SKIP_AUTH_LOGOUT_HEADER) });
        }

        if (!shouldSkipActivity) {
            this.loaderService.show();
        }

        return this.loginService.sessionUser.pipe(
            take(1),
            exhaustMap((user) => {
                if (!user) {
                    return next.handle(cleanedReq).pipe(
                        catchError((error) => {
                            const err = error?.error;
                            const message =
                                typeof err === 'string' ? err : err?.status?.message;

                            this.toasterService.error(message, 'Error', {
                                tapToDismiss: true,
                            });

                            return throwError(() => error);
                        }),
                        finalize(() => {
                            if (!shouldSkipActivity) {
                                this.loaderService.hide();
                            }
                        }),
                    );
                }

                const modifiedReq = cleanedReq.clone({
                    setHeaders: {
                        'Content-Type': 'application/json; charset=utf-8',
                        'X-Auth-Key': user['X-Auth-Key'],
                        source: 'portal',
                    },
                });

                return next.handle(modifiedReq).pipe(
                    tap({
                        error: (err: any) => {
                            if (err instanceof HttpErrorResponse) {
                                this.errorHandlerService.handleError(err);

                                if (err.status === 401 && !shouldSkipAuthLogout) {
                                    this.loginService.logout();
                                }
                            }
                        },
                    }),
                    finalize(() => {
                        if (!shouldSkipActivity) {
                            this.loaderService.hide();
                        }
                    }),
                );
            }),
        );
    }
}
